import { SearchableOtherBets } from 'components/OtherBets/SearchableOtherBets';
import { useState } from 'react';
import { RedCards } from 'components/RedCards/RedCards';
import { useTranslation } from 'react-i18next';
import { liveClock } from 'utils/sportEvent.utils';
import classNames from 'classnames';
import { BottomSheet } from 'components/mobile/BottomSheet/BottomSheet';
import { MatchInsights } from 'components/MatchInsights/MatchInsights';
import { LiveEvent as LiveEventDto } from 'types/LiveEvent';
import styles from './ModernLiveSheet.module.css';

type View = 'markets' | 'statistics' | 'h2h';

/**
 * Modern layout: a live match in a sheet over the live list - the score and
 * minute on top, then Markets | Statistics | Head to head as on the prematch
 * match page. The event comes from the list, so prices and score stay live.
 */
export const ModernLiveSheet = ({
  event,
  close,
}: {
  event: LiveEventDto;
  close: () => void;
}) => {
  const { t } = useTranslation();
  const [view, setView] = useState<View>('markets');
  const labels: Record<View, string> = {
    markets: t('modern_tab_markets'),
    statistics: t('match_statistics_label'),
    h2h: t('match_h2h'),
  };

  return (
    <BottomSheet tall close={close}>
      <div className={styles.hero}>
        <div className={styles.league}>{event.league}</div>
        <div className={styles.teams}>
          <span className={styles.team}>
            {event.home.name}
            <RedCards
              count={event.homeRedCards}
              yellow={event.liveCounts?.yellow?.[0]}
            />
          </span>
          <span className={styles.score}>
            <strong>
              {event.homeGoals} : {event.awayGoals}
            </strong>
            {event.time && <small>{liveClock(event, t)}</small>}
          </span>
          <span className={styles.team}>
            {event.away.name}
            <RedCards
              count={event.awayRedCards}
              yellow={event.liveCounts?.yellow?.[1]}
            />
          </span>
        </div>
      </div>

      <div className={styles.views} role="tablist">
        {(['markets', 'statistics', 'h2h'] as const).map((v) => (
          <button
            type="button"
            key={v}
            role="tab"
            aria-selected={view === v}
            className={classNames(styles.view, view === v && styles.viewOn)}
            onClick={() => setView(v)}
          >
            {labels[v]}
          </button>
        ))}
      </div>

      <div className={styles.body}>
        {view === 'markets' &&
          (event.bets.length > 0 ? (
            <SearchableOtherBets match={event} bets={event.bets} />
          ) : (
            <p className={styles.empty}>{t('no_bet_offer_message')}</p>
          ))}
        {view === 'statistics' && (
          <div className={styles.card}>
            <MatchInsights
              fi={event.fi}
              hideScoreboard
              h2h="exclude"
              liveOdds={<SearchableOtherBets match={event} bets={event.bets} />}
            />
          </div>
        )}
        {view === 'h2h' && (
          <div className={styles.card}>
            <MatchInsights fi={event.fi} hideScoreboard h2h="only" />
          </div>
        )}
      </div>
    </BottomSheet>
  );
};
